import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { EMOJI_NO_COUNTRY_FLAGS } from "../../utils/emojiPicker";
import i18nDe from "@emoji-mart/data/i18n/de.json";
import MaterialIcon from "../MaterialIcon";
import { splitMenuRows } from "../../utils/chatActionRows";
import { emojiUnicode } from "../../utils/encryptDecryptFunction";
import "./chat-pro.css";

// Message menu of the mobile Pro chat (v2/pro/MessageMenuPro.js ProMessageMenu), adapted to the
// web: opened by right-click on a bubble or the "more" button of its hover bar, it floats next to
// the pointer instead of sliding up from the bottom.
//   reaction bar  👍 ✅ 👀 🙏 💪 ❤️ + "more" (the full picker replaces the action card)
//   action card   4-tile top row (Reply · Forward · Copy | Save · Bookmark) and the rows below
//                 (Translate · Mark as unread · Select · Info · Edit · Delete in red)
// Rows come from utils/chatActionRows (the mobile conditions); this file only draws them.

export const QUICK_REACTIONS = ["👍", "✅", "👀", "🙏", "💪", "❤️"];

const MARGIN = 8;

export default function MessageMenuPro({
  open,
  anchor,
  rows = [],
  reactions,
  myReactions = [],
  onClose,
  internal = false,
  isGerm = false,
  labels = {},
}) {
  const menuRef = useRef(null);
  const [allEmoji, setAllEmoji] = useState(false);
  const [pos, setPos] = useState(null);

  useEffect(() => {
    if (!open) {
      setAllEmoji(false);
      setPos(null);
    }
  }, [open]);

  const place = useCallback(() => {
    const el = menuRef.current;
    if (!el || !anchor) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left;
    let top;
    if (anchor.rect) {
      const r = anchor.rect;
      left = anchor.alignEnd ? r.right - w : r.left;
      top = r.bottom + 6;
      if (top + h > vh - MARGIN) top = r.top - h - 6;
    } else {
      left = anchor.x;
      top = anchor.y;
      if (left + w > vw - MARGIN) left = anchor.x - w;
      if (top + h > vh - MARGIN) top = anchor.y - h;
    }
    left = Math.max(MARGIN, Math.min(left, vw - w - MARGIN));
    top = Math.max(MARGIN, Math.min(top, vh - h - MARGIN));
    setPos({ left, top });
  }, [anchor]);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place, allEmoji, rows.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    const onScroll = (e) => {
      if (menuRef.current && e.target && menuRef.current.contains(e.target)) return;
      onClose();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", onClose);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onClose);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open, onClose]);

  if (!open) return null;

  const { top, rest } = splitMenuRows(rows);
  const mine = new Set((myReactions || []).map((h) => String(h).toLowerCase()));
  const run = (row) => () => {
    onClose();
    if (row && typeof row.onPress === "function") row.onPress();
  };

  return createPortal(
    <div
      className="kjc-menu-overlay"
      onMouseDown={(e) => {
        if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        onClose();
      }}
    >
      <div
        ref={menuRef}
        className={`kjc-menu${internal ? " kjc-internal" : ""}`}
        style={pos ? { left: pos.left, top: pos.top } : { left: -9999, top: -9999 }}
        role="menu"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {reactions ? (
          <div className="kjc-menu__reacts">
            {QUICK_REACTIONS.map((glyph) => {
              let hex = "";
              try {
                hex = emojiUnicode(glyph).toLowerCase();
              } catch (_) {
                hex = "";
              }
              const on = mine.has(hex);
              return (
                <button
                  key={glyph}
                  type="button"
                  className={`kjc-menu__react${on ? " is-on" : ""}`}
                  onClick={() => {
                    onClose();
                    reactions.onReact(glyph);
                  }}
                  aria-pressed={on}
                >
                  {glyph}
                </button>
              );
            })}
            <button
              type="button"
              className={`kjc-menu__react kjc-menu__react--more${allEmoji ? " is-on" : ""}`}
              onClick={() => setAllEmoji((v) => !v)}
              aria-expanded={allEmoji}
              aria-label={labels.moreReactions}
              title={labels.moreReactions}
            >
              <MaterialIcon icon="add_reaction" size={22} weight={300} />
            </button>
          </div>
        ) : null}
        {allEmoji && reactions ? (
          <div className="kjc-menu__picker">
            <Picker
              data={data}
              i18n={isGerm ? i18nDe : undefined}
              theme="light"
              previewPosition="none"
              skinTonePosition="none"
              navPosition="bottom"
              perLine={8}
              maxFrequentRows={1}
              emojiSize={22}
              emojiButtonSize={32}
              set="native"
              noCountryFlags={EMOJI_NO_COUNTRY_FLAGS}
              onEmojiSelect={(e) => {
                const native = e && (e.native || e.emoji);
                if (!native) return;
                onClose();
                reactions.onReact(native);
              }}
            />
          </div>
        ) : (
          <div className="kjc-menu__card">
            {top.length ? (
              <div className={`kjc-menu__top${rest.length ? " has-rows" : ""}`}>
                {top.map((row) => (
                  <button key={row.key} type="button" role="menuitem" className="kjc-menu__tile" onClick={run(row)}>
                    <MaterialIcon icon={row.icon} size={24} weight={300} fill={row.filled ? 1 : 0} />
                    <span>{row.label}</span>
                  </button>
                ))}
              </div>
            ) : null}
            {rest.map((row) => (
              <button
                key={row.key}
                type="button"
                role="menuitem"
                className={`kjc-menu__row${row.danger ? " is-danger" : ""}`}
                onClick={run(row)}
              >
                <MaterialIcon icon={row.icon} size={22} weight={300} />
                <span>{row.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
